import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';
import { EntitiesListRowProps } from '../entities-list/ui/EntitiesListRow/EntitiesListRow';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName, selectSelectedFeatures } from '@redux/selectors/selectors';
import { useEntitiesLoader } from '../entities-list/model/useEntitiesLoader';
import EntitiesList, { EntitiesListProps } from '../entities-list/ui/EntitiesList/EntitiesList';
import EntitiesControls, { EntitiesControlsProps } from '../EntitiesControls/EntitiesControls';
import './entities-loader.scss';
import { flewToPoint, setBoundsToFit, setSourceReloadRequired } from '@redux/map/map-reducer';
import { getFeatureBbox, getFeatureCenter } from '@utils/mapUtils';

type Props<T extends BaseEntityNameLess> = {
    entityName: string;
    layerEntityName?: string;
    serviceName: string;
    parentId?: string;
    newEntityUrl?: string;
    showNewButton?: boolean;
    onEntitiesLoadedFn?: (entities: T[]) => void;
    zoomToRelatedFeatureBbox?: boolean;
    onRowClick?: (entity: T) => Promise<void>;
    onDeleteFn?: (entities: any[]) => Promise<void> | void;
    controls?: (props: Pick<ReturnType<typeof useEntitiesLoader>, 'loadData'>) => React.ReactNode;
} & Pick<EntitiesListProps<T>, 'menuActions'> &
    Pick<EntitiesListRowProps<T>, 'hasLink'> &
    Omit<EntitiesListRowProps<T>, 'entity' | 'deleteFn' | 'onRowClick'>;

const EntitiesLoader = <T extends BaseEntityNameLess>({
    entityName,
    layerEntityName,
    serviceName,
    parentId,
    form,
    newEntityUrl,
    // showEditIcon = true,
    showNewButton = true,
    getUrlFunc,
    menuActions,
    onEntitiesLoadedFn,
    zoomToRelatedFeatureBbox = true,
    onRowClick: onRowClickProps,
    onDeleteFn,
    controls: Controls,
    hasLink = true,
}: Props<T>) => {
    const layerId = useTypedSelector(state => selectMapLayerIdByEntityName(state, layerEntityName));
    const selectedFeatures = useTypedSelector(selectSelectedFeatures);
    const {
        loading,
        deleting,
        entities,
        loadData,
        deleteFn,
        loadFeatureOnRowClick,
        searchPhrase,
        setSearch,
    } = useEntitiesLoader<T>({
        entityName: entityName!,
        serviceName: serviceName!,
        parentId,
    });
    const dispatch = useDispatch();

    useEffect(() => {
        onEntitiesLoadedFn?.(entities);
    }, [entities]);

    const deleteHandler = useCallback(
        async (id: string) => {
            await deleteFn(id);
            if (layerId) {
                dispatch(setSourceReloadRequired({ sourceId: layerId, isReloadRequired: true }));
            }
            await onDeleteFn?.(entities);
        },
        [layerId, deleteFn, onDeleteFn, entities]
    );

    const onRowClick = useCallback(
        async (args: T) => {
            await onRowClickProps?.(args);

            if (zoomToRelatedFeatureBbox) {
                const feature = await loadFeatureOnRowClick(args);
                if (feature) {
                    if (feature.geometry.type === 'Point') {
                        const [lng, lat] = getFeatureCenter(feature);
                        if (lng && lat) {
                            dispatch(flewToPoint({ lng, lat, zoom: 15 }));
                        }
                    } else {
                        const bbox = getFeatureBbox(feature);
                        dispatch(setBoundsToFit(bbox));
                    }
                }
            }
        },
        [entityName, layerId, zoomToRelatedFeatureBbox, onRowClickProps]
    );

    return (
        <div className="entities-loader">
            <EntitiesControls
                searchPhrase={searchPhrase}
                onSearch={setSearch}
                newEntityUrl={newEntityUrl}
                showNewButton={showNewButton}
                disableSearchField={deleting || (!searchPhrase && !entities.length)}
                controls={Controls ? <Controls loadData={loadData} /> : null}
            />
            <EntitiesList<T>
                loading={loading || deleting}
                entities={entities}
                onRowClick={onRowClick}
                hasLink={hasLink}
                form={form}
                getUrlFunc={getUrlFunc}
                deleteFn={deleteHandler}
                loadDataFn={loadData}
                selectedId={selectedFeatures?.[0]?.id}
                menuActions={menuActions}
            />
        </div>
    );
};

export default EntitiesLoader;
